/* ------------------------------------------------------------------------
  Copyright (c) 2016, VoiceVibes, Inc

  VoiceVibes, Inc - PROPRIETARY/CONFIDENTIAL
  All contents of this file are proprietary and confidential. No part of
  any of the contents of this file may be disclosed in any manner to
  a third party without the prior written consent of VoiceVibes, Inc.
  Unauthorized copying of this file, via any medium is strictly prohibited.
----------------------------------------------------------------------------- */

<template>
  <div
    :ref="'comentsRef' + commentRef"
  >
    <h2
      id="feedback-nav"
      class="sr-only"
    >
      Feedback Navigation
    </h2>
    <nav aria-labelledby="feedback-nav">
      <ul
        class="nav nav-tabs"
      >
        <li
          :class="{ active: tab === 'comments' }"
        >
          <a
            id="commentslink"
            ref="commentslink"
            href="#"
            @click.prevent="loadTab('comments')"
            @keypress="loadTab('comments', $event)"
          >
            Comments
          </a>
        </li>
        <li
          v-if="showAddScore"
          :class="{ active: tab === 'add score' }"
        >
          <a
            id="addscorelink"
            ref="addscorelink"
            href="#"
            @click.prevent="loadTab('add score')"
            @keypress="loadTab('add score', $event)"
          >
            Add a Score
          </a>
        </li>
      </ul>
    </nav>
    <div
      id="feedback-container-parent"
      :class="{ hidden: tab !== 'comments' }"
      class="tips tab-well"
      aria-labelledby="commentsheader"
    >
      <h2
        v-if="feedback.length > 0"
        id="commentsheader"
        class="sr-only"
      >
        Coach/Peer Feedback
      </h2>
      <transition-group
        v-if="loaded"
        name="feedback"
        tag="div"
      >
        <div
          v-for="comment in feedback"
          :ref="'feedback-container-' + comment.feedbackId"
          :key="comment.feedbackId"
          :class="getFeedbackContainerClass(comment)"
        >
          <div
            :ref="'feedback-' + comment.feedbackId"
            :class="comment.isManager ? ' managerFeedback' : ' speakerFeedback'"
          >
            <div class="feedback-edit-controls-container">
              <div
                v-if="comment.userId == user.userId || comment.regionEnd"
                class="feedback-edit-controls"
              >
                <div v-if="comment.regionEnd">
                  A region was marked:
                </div>
                <a
                  v-if="comment.regionEnd"
                  :id="'showRegionLink-' + comment.feedbackId"
                  :ref="'showRegionLink-' + comment.feedbackId"
                  href="#"
                  class="showRegionLink"
                  @click.prevent="showRegion(comment.regionStart, comment.regionEnd, comment.feedbackId)"
                >
                  Show Region
                </a>
                <div class="float-icons">
                  <span
                    v-if="comment.userId == user.userId"
                    tabindex="0"
                    aria-label="update feedback below"
                    role="button"
                    @click.prevent="showUpdate(comment)"
                    @keypress="showUpdate(comment, $event)"
                  >
                    <svg class="svgicon icon-pencil">
                      <use xlink:href="/static/svg-symbols.svg#icon-pencil" />
                    </svg>
                  </span>
                  &nbsp;
                  <span
                    v-if="comment.userId == user.userId"
                    tabindex="0"
                    aria-label="delete feedback below"
                    role="button"
                    @click.prevent="showDelete(comment)"
                    @keypress="showDelete(comment, $event)"
                  >
                    <svg class="svgicon icon-trash">
                      <use xlink:href="/static/svg-symbols.svg#icon-trash" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
            <div class="feedback-update-comment">
              <br>
              <form @submit.prevent="update(comment)">
                <label
                  :for="'updated-comment-' + comment.feedbackId"
                  class="field-label"
                >
                  Edit your comment
                </label>
                <textarea
                  :id="'updated-comment-' + comment.feedbackId"
                  v-model="comment.comment"
                  class="edit-feedback"
                />
                <br>
                <loading-button :loading="saving1">
                  Save Changes
                </loading-button>
              </form>
            </div>
            <br>
            <p
              class="feedback-comment"
              style="white-space: pre-line;"
            >
              {{ comment.comment }}
            </p>
            <b class="feedback-attribution">
              - {{ comment.name || user.name }},
              {{ comment.createdDate | date }}
            </b>
            <div class="feedback-confirm-delete">
              <a
                :id="'deleteCommentConfirmed' + comment.feedbackId"
                href="#"
                @click.prevent="deleteFeedback(comment)"
              >
                Delete comment
              </a> | <a
                href="#"
                @click.prevent="cancelDelete(comment)"
              >
                Cancel
              </a>
            </div>
          </div>
          <hr>
        </div>
      </transition-group>

      <form
        id="feedbackForm"
        aria-labelledby="commentOnThisRecording"
        @submit.prevent="saveFeedback"
      >
        <label
          id="commentOnThisRecording"
          for="feedbackComment"
          class="form-group-label"
        >
          Comment on this Recording
        </label>
        <div class="form-group">
          <textarea
            id="feedbackComment"
            v-model="feedbackComment"
            class="form-control"
            placeholder="enter your feedback here"
            required
          />
        </div>
        <div class="form-group">
          <label
            for="associateComment"
            class="control-label lower"
          >
            <input
              id="associateComment"
              v-model="associated"
              type="checkbox"
              value="1"
              @change="markRegionChanged"
            >
            Mark a region of the audio
          </label>
        </div>
        <div v-if="associated">
          <h3 id="addingARegionHeader">
            Adding a Region to Your Comment
          </h3>
          <div v-if="!accessibleRegioning">
            Using your mouse, click and drag on the audio signal to highlight a pink region at
            least half a second long to
            associate it with your written feedback above. Once created, you
            can click and drag to adjust the start or end position of the region; or
            you can <a
              href="#"
              @click.prevent="enableDragSelection"
            >
              click here to start over.
            </a><br><br>
            If you would like to add a region using only your keyboard,
            <a
              href="#"
              @click.prevent="enableAccessibleRegioning"
            >
              click this link.
            </a>
          </div>
          <div v-if="accessibleRegioning">
            A pink region has been highlighted automatically in the middle of the audio signal.

            You may set the start time and end times of the region in the fields below.

            Clicking the Play Region link will play the currently selected region on a loop.

            <div class="row row-no-padding">
              <div class="regionRow col-xs-4">
                <label
                  for="regionStart"
                  class="control-label"
                >
                  Region Start
                </label>
              </div>
              <div class="regionRow col-xs-4">
                <input
                  id="regionStart"
                  v-model="regionStart"
                  type="text"
                  @change="regionChangedByTextbox"
                >
              </div>
              <div class="regionRow col-xs-4">
&nbsp;
              </div>
            </div>

            <div class="row row-no-padding">
              <div class="regionRow col-xs-4">
                <label
                  for="regionEnd"
                  class="control-label"
                >
                  Region End
                </label>
              </div>
              <div class="regionRow col-xs-4">
                <input
                  id="regionEnd"
                  v-model="regionEnd"
                  type="text"
                  @change="regionChangedByTextbox"
                >
              </div>
              <div class="regionRow col-xs-4">
                <a
                  href="#"
                  @click.prevent="playLoop"
                >
                  {{ regionLink }}
                </a>
              </div>
            </div>
          </div>
        </div>
        <br>
        <loading-button :loading="savingFeedback">
          Save
        </loading-button>
      </form>
    </div>

    <div
      v-if="showAddScore"
      id="add-scores-container-parent"
      :class="{ hidden: tab !== 'add score' }"
      class="tips tab-well"
      aria-labelledby="addscoreheader"
    >
      <h2
        id="addscoreheader"
        class="sr-only"
      >
        Add a Score
      </h2>

      <!-- Delete an Existing Score -->
      <form
        v-if="myScore"
        id="deleteScore"
        aria-labelledby="deleteThisScore"
        @submit.prevent="deleteScore"
      >
        <span class="deleteScoreIntro">
          On {{ myScore.createdDate | date }} you assigned the following scores to this recording:
        </span>
        <br><br>
        <!-- Scores -->
        <div
          v-for="score in scorecardQuestionsForRecording"
          v-if="!score.hidden"
          :key="score.label + '_' + score.value"
          class="form-group"
        >
          <div aria-hidden="true">
            <label style="font-size: 14px; font-weight: bold; display: block;">
              {{ score.label }}
            </label>
            <!-- eslint-disable-next-line -->
            <div class="scoreDescription">{{ getScoreText(score) }}</div>
            <star-rating
              inactive-color="#fff"
              :increment="0.5"
              :border-width="1"
              border-color="#cccccc"
              active-color="#EFA042"
              :star-size="15"
              :rating="score.myScore ? parseFloat(score.myScore) / 2 : 0"
              :read-only="true"
            /><br>
          </div>
          <div class="sr-only">
            Your score for {{ score.label }} was {{ score.myScore }} out of 10.
          </div>
        </div>

        If you would like to edit these scores, please delete them and resubmit the correct values.<br><br>
        <loading-button :loading="savingFeedback">
          Delete Scores
        </loading-button>
      </form>

      <!-- Add a New Score -->
      <form
        v-if="!myScore"
        id="scoreForm"
        aria-labelledby="scoreThisRecording"
        @submit.prevent="saveScore"
      >
        <h2
          id="scoreThisRecording"
          style="margin-bottom: 1em; margin-top: 0.2em;"
        >
          Score this Recording
        </h2>
        <div class="sr-only">
          Please provide scores for each category on a scale from 1 to 10, where 1 means Poor and 10 means
          excellent.
        </div>
        <div
          v-for="score in scorecardQuestionsForRecording"
          v-if="!score.hidden"
          :key="score.label + '_' + score.value"
          class="core-score form-group"
        >
          <label
            :id="'radio' + score.id"
            style="font-size: 14px; font-weight: bold; display: block;"
          >
            {{ score.label }}
          </label>
          <!-- eslint-disable-next-line -->
          <div class="scoreDescription">{{ getScoreText(score) }}</div>
          <span
            v-for="i in [1,2,3,4,5,6,7,8,9,10]"
            :key="i"
            class="form-group"
          >
            <label
              :style="(i>1) ? 'margin-left: 1.2em;' : ''"
              :for="score.id + i + 'radio'"
            >
              {{ i }}
              <input
                v-model="responses[score.id]"
                :aria-labelledby="'radio' + score.id"
                required
                style="display: block;"
                type="radio"
                :value="i"
                :name="score.id"
                :for="score.id + i + 'radio'"
              >
            </label>
          </span><br>
          <span
            aria-hidden="true"
            class="descriptor"
          >
            Poor
          </span>
          <span
            style="padding-left: 200px;"
            aria-hidden="true"
            class="descriptor"
          >
            Excellent
          </span>
        </div>
        <br>
        <loading-button :loading="savingScores">
          Save
        </loading-button>
      </form>
    </div>
  </div>
</template>

<style lang="less" scoped>
  .deleteScoreIntro {
    font-size: 12px;
  }
  .descriptor {
    font-size: 10px;
  }
  .num-raters {
    padding: 5px;
  }
  .core-score {
    margin-bottom: 2em;
  }
  #addingARegionHeader {
    font-size: 18px;
  }
  hr {
    border-color: #ccc;
  }
  #scoreForm {
    min-width: 340px;
    overflow: visible;
  }
  .scoreDescription {
    max-height: 200px;
    overflow-y: auto;
    margin-bottom: 1em;
    font-size: 12px;
  }
  #regionStart, #regionEnd {
    width: 6em;
  }
  .regionRow {
    margin-top: 1.1em;
  }

  .svgicon {
    display: inline-block;
    stroke-width: 0;
    stroke: currentColor;
    fill: currentColor;
  }
  .icon-pencil {
    width: 18px; height: 18px;
  }
  .icon-trash {
    width: 18px; height: 18px;
  }
  .feedback-edit-controls-container {
    position: relative;
    top: 4px;
    z-index: 20;
  }
  .feedback-edit-controls {
    a, div {
      float: left;
      padding-right: 5px;
    }
    .float-icons {
      float: right;
    }
    i {
      padding-right: 5px;
    }
    padding-right: 10px;
    padding-bottom: 10px;
    top: 0;
    right: 10px;
  }
  hr {
    margin: 11px;
  }
  .managerFeedback {
    background-color: #fefff1;
  }
  .feedback-comment, .feedback-attribution {
    position: relative;
    bottom: 15px;
    z-index: 10;
  }
  .deleting-feedback {
    .feedback-comment, .feedback-attribution {
      opacity: 0.5;
    }
    .feedback-confirm-delete {
      display: block;
    }
  }
  .feedback-confirm-delete {
    display: none;
    position: absolute;
    left: 30px;
    top: 15px;
    opacity: 1.0;
    border: 2px solid #ccc;
    background-color: white;
    padding: 15px;
    z-index: 20;
  }
  .managerFeedback, .speakerFeedback {
    position: relative;
    padding: 0 0 0 8px;
    word-wrap: break-word;
  }
  .edit-feedback {
    height: 150px;
    width: 100%;
    margin-top: 5px;
  }
  #feedbackForm {
    textarea {
      height: 150px;
    }
    margin-top: -10px;
    padding: 0 10px 10px 10px;
  }
  .feedback-edit-in-progress {
    .feedback-comment, .feedback-attribution {
      display: none;
    }
  }
  .feedback-item {
    position: relative;
    display: inline-block;
    margin-right: 10px;
    width: 100%;
    border: 1px solid white;

    .feedback-update-comment {
      display: none;
    }
  }
  .feedback-enter-active, .feedback-leave-active {
    transition: all 1s;
  }
  .feedback-enter, .feedback-leave-to /* .list-leave-active below version 2.1.8 */ {
    opacity: 0;
    transform: translateY(30px);
  }
  .feedback-deleted {
    opacity: 0;
    transition: opacity 1s linear;
  }
</style>

<script>
import StarRating from 'vue-star-rating';
import ScorecardMixin from '../specials/ScorecardMixin';

export default {
  components: { StarRating },
  mixins: [ScorecardMixin],
  props: {
    'recording': Object,
    'wavesurfer': Object,
    'activeGroup': Object,
    'isGroupManager': Boolean,
    'isMyRecording': Boolean
  },
  data() {
    return {
      tab: 'comments',
      loaded: false,
      saving1: false,
      savingFeedback: false,
      savingScores: false,
      feedbackComment: null,
      commentRef: 1,
      currentlyEditing: null,

      responses: {
        overall: null,
        organization: null,
        delivery: null,
        xfactor: null,
      },

      // Score stuff
      hideScore: {},

      // Vars for regions
      wavesurferEventIsBound: null, // Gets set to recording.recordingId
      accessibleRegioning: false,
      associated: false,
      region: null,
      defaultRegionStart: null,
      defaultRegionEnd: null,
      regionIdCounter: 1, // Ensures each region we create has a unique ID
      regionStart: null,
      regionEnd: null,
      regionLink: 'Play region',
      playing: false,
    };
  },
  watch: {
    'associated': 'resetWavesurfer',
  },
  updated() {
    // Check to see if the recording changed and uncheck highlight region checkbox
    if (this.wavesurferEventIsBound !== this.recording.recordingId) {
      this.associated = false;
    }

    // Check for invalid tab
    if ((this.tab === 'scores' && !this.showScoreCard) ||
        (this.tab === 'add score' && !this.showAddScore)) {
      this.tab = 'comments';
    }
  },
  created() {
    this.loaded = false;
    const lookups = [
      this.$store.dispatch('listGroupFeedback')
    ];
    window.Promise.all(lookups).then(() => {
      this.loaded = true;
    });
  },
  methods: {
    markRegionChanged() {
      this.resetShowRegionLinks();
      this.wavesurfer.clearRegions();
    },
    resetShowRegionLinks() {
      // Reset any "Hide Region" links to say "Show Region"
      const links = document.getElementsByClassName('showRegionLink');
      _.forEach(links, (link) => link.innerHTML = 'Show Region');

      // Clear out the active region, if any, from metrics
      window.wavesurferHotspotRendered = false;
    },
    showRegion(start, end, feedbackId) {
      this.resetShowRegionLinks();

      const regionId = start + '_' + end + '_';
      const regionExists = !!(regionId in this.wavesurfer.regions.list);

      this.associated = false;
      this.accessibleRegioning = false;
      this.wavesurfer.clearRegions();

      if (regionExists) {
        // All we do is hide the region if it's already showing
        return;
      }

      const loop = !window.wavesurferIsPlaying;
      if (!window.wavesurferIsPlaying) {
        this.wavesurfer.seekTo(0);
        this.wavesurfer.skipForward(start);
      }

      // Update the link text
      this.$refs['showRegionLink-' + feedbackId][0].innerHTML = 'Hide Region';

      // Otherwise, show the new region
      this.$nextTick(() => {
        this.region = this.wavesurfer.addRegion({
          id: regionId,
          start,
          end,
          loop,
          drag: false,
          resize: false,
        });
        this.$announcer.set('The region was highlighted. Press play to play the region on a loop or click Show Region again to deselect it');
      });
    },
    regionDisableAssociated(region) {
      if (region.id.indexOf('newRegion') === -1) {
        if (this.associated) {
          this.associated = false;
        }
      }
    },
    regionUpdated(region) {
      if (region.id.indexOf('newRegion') === -1) {
        return true;
      }

      // If they accidentally made a region so small it's not clickable, clear it out so they can try again
      if ((region.end - region.start) < 0.5) {
        // Clear the region and re-enable drag selection
        return this.enableDragSelection();
      }

      this.regionEnd =  Math.round(region.end * 100) / 100;
      this.regionStart = Math.round(region.start * 100) / 100;

      this.wavesurfer.disableDragSelection();
    },
    enableAccessibleRegioning() {
      this.regionStart = this.defaultRegionStart;
      this.regionEnd = this.defaultRegionEnd;

      this.accessibleRegioning = true;
      this.addRegion(this.defaultRegionStart, this.defaultRegionEnd);
      this.$nextTick(() => {
        document.getElementById('regionStart').focus();
      });
    },
    regionChangedByTextbox() {
      // If they moved start to after end, push end to the end of the recording
      if (window.parseFloat(this.regionStart) > window.parseFloat(this.regionEnd)) {
        this.regionEnd = this.recording.duration;
      }

      const roundedStart = Math.round(this.regionStart * 100) / 100;
      const roundedEnd = Math.round(this.regionEnd * 100) / 100;

      if (isNaN(roundedEnd) || isNaN(roundedStart) || roundedStart > roundedEnd || roundedEnd > this.recording.duration) {
        this.regionEnd = this.defaultRegionEnd;
        this.regionStart = this.defaultRegionStart;
        return;
      }

      if (this.regionStart != roundedStart) {
        this.regionStart = roundedStart;
      }
      if (this.regionEnd != roundedEnd) {
        this.regionEnd = roundedEnd;
      }

      // Show the new region
      this.addRegion(roundedStart, roundedEnd);
    },
    addRegion(start, end) {
      const loop = !window.wavesurferIsPlaying;

      this.wavesurfer.clearRegions();

      this.region = this.wavesurfer.addRegion({
        id: 'newRegion' + this.regionIdCounter++,
        start,
        end,
        loop,
        drag: false,
      });
    },
    enableDragSelection() {
      this.regionStart = null;
      this.regionEnd = null;

      this.wavesurfer.clearRegions();
      this.wavesurfer.disableDragSelection();

      this.wavesurfer.enableDragSelection({
        id: 'newRegion' + this.regionIdCounter++,
        loop: true,
        drag: false,
      });
    },
    playLoop() {
      if (this.playing) {
        this.wavesurfer.stop();
        this.regionLink = 'Play region';
      } else {
        this.region.play();
        this.regionLink = 'Pause region';
      }
      this.playing = !this.playing;
    },
    resetWavesurfer() {
      // Reset everything and stop playback
      this.playing = false;
      this.accessibleRegioning = false;
      this.regionLink = 'Play region';
      this.wavesurfer.stop();

      if (!this.associated) {
        this.regionStart = null;
        this.regionEnd = null;
        this.wavesurfer.disableDragSelection();
        return;
      }

      this.wavesurfer.clearRegions();

      const duration = this.recording.duration * 0.25;
      const start = Math.round((this.recording.duration / 2 - duration / 2) * 100) / 100;
      const end = Math.round((start + duration) * 100) / 100;

      // Save the defaults and set form fields
      this.defaultRegionStart = start;
      this.defaultRegionEnd = end;
      this.regionStart = start;
      this.regionEnd = end;

      if (this.wavesurferEventIsBound !== this.recording.recordingId) {
        this.wavesurferEventIsBound = this.recording.recordingId;
        this.wavesurfer.on('region-update-end', (region) => this.regionUpdated(region));
        this.wavesurfer.on('region-created', (region) => this.regionDisableAssociated(region));
      }

      this.enableDragSelection();
    },
    getFeedbackContainerClass(comment) {
      if (comment.feedbackId === this.currentlyEditing) {
        return 'feedback-edit-in-progress';
      }
      return comment.userId === this.user.userId ? 'my-feedback-item feedback-item' : 'feedback-item';
    },
    showDelete(feedback, evt) {
      if (evt && evt.key && evt.key === 'Tab') {
        // Firefox bug fix
        return true;
      }

      // For keyboard nav, use alert() as a confirmation
      if (evt && evt.key) {
        if (window.confirm('Delete this feedback?')) {
          this.deleteFeedback(feedback);
        }
        return true;
      }

      const elem = this.$refs['feedback-' + feedback.feedbackId][0];
      elem.className += ' deleting-feedback';
      document.getElementById('deleteCommentConfirmed' + feedback.feedbackId).focus();
    },
    showUpdate(feedback, evt) {
      if (evt && evt.key && evt.key === 'Tab') {
        // Firefox bug fix
        return true;
      }
      // First, close all the other editors
      this.feedback.forEach((f) => f.status = '');

      this.currentlyEditing = feedback.feedbackId;
      this.commentRef++;
      this.$nextTick(() => document.getElementById('updated-comment-' + feedback.feedbackId).focus());
    },
    cancelDelete(feedback) {
      const elem = this.$refs['feedback-' + feedback.feedbackId][0];
      elem.className = elem.className.replace('deleting-feedback', '');
    },
    deleteFeedback(feedback) {
      const elem = this.$refs['feedback-container-' + feedback.feedbackId][0];
      this.$store.dispatch('deleteFeedback', feedback).then(() => {
        this.$announcer.set('Feedback deleted');
        elem.className += ' feedback-deleted';
        window.setTimeout(() => {
          elem.style.display = 'none';
        }, 1000);
      });
      this.$emit('feedbackModified');
    },
    deleteScore() {
      if (!window.confirm('Delete these scores?')) return;

      const promises = [
        this.$store.dispatch('deleteFeedback', this.myScore)
      ];
      if (this.hasCustomScorecard && this.myResponse) {
        promises.push(this.$store.dispatch('deleteResponse', this.myResponse));
      }

      window.Promise.all(promises).then(() => {
        this.commentRef++;
        this.$forceUpdate();
        this.$announcer.set('Score deleted');
        this.$emit('feedbackModified');
      });
    },
    reset() {
      this.savingFeedback = false;
      this.savingScores = false;
      this.feedbackComment = null;
      this.associated = false;
      this.accessibleRegioning = false;
      this.overall = null;
      this.organization = null;
      this.delivery = null;
      this.xfactor = null;
      this.wavesurfer.clearRegions();
      this.resetWavesurfer();
    },
    saveFeedback() {
      this.savingFeedback = true;

      const feedbackData = {
        comment: this.feedbackComment,
        recordingId: this.recording.recordingId,
        groupId: this.recording.groupId,
      };

      if (this.associated && this.regionStart && this.regionEnd &&
          (this.regionStart != this.defaultRegionStart || this.regionEnd != this.defaultRegionEnd)) {
        // We discard the region, if they never changed it from the defaults
        feedbackData.regionStart = this.regionStart;
        feedbackData.regionEnd = this.regionEnd;
      }

      return this.$store.dispatch('addFeedback', feedbackData).then(() => {
        this.reset();
        this.commentRef++;
        this.$announcer.set('Feedback saved');
        this.$emit('feedbackModified');
      });
    },
    saveScore() {
      this.savingScores = true;

      // First, save the coreScores as feedback
      const feedbackData = {
        recordingId: this.recording.recordingId,
        groupId: this.recording.groupId,
        scoreOverall: this.responses.overall,
      };
      if (this.responses.organization !== null) {
        feedbackData.scoreOrganization = this.responses.organization;
      }
      if (this.responses.delivery !== null) {
        feedbackData.scoreDelivery = this.responses.delivery;
      }
      if (this.responses.xfactor !== null) {
        feedbackData.scoreXfactor = this.responses.xfactor;
      }

      const allDone = () => {
        this.reset();
        this.tab = 'scores';
        this.commentRef++;
        this.$forceUpdate();
        this.$announcer.set('Your score was recorded');
        this.$emit('feedbackModified');

        // TODO: This fixes a bug where the "Add a Score" pane is replaced
        // by an empty pane. This is a hack and there s/b a better fix
        this.$nextTick(() => document.getElementById('addscorelink').click());
      };

      let scorecardResponse;
      if (this.hasCustomScorecard) {
        const responses = [];
        _.forEach(this.responses, (response, id) => {
          if (response === null) return;
          responses.push({id, response});
        });

        scorecardResponse = {
          recordingId: this.recording.recordingId,
          scorecardId: this.recording.assignment.scorecard.id,
          responses,
        };
      }

      let promises = [
        this.$store.dispatch('addFeedback', feedbackData),
      ];
      if (this.hasCustomScorecard) {
        promises.push(this.$store.dispatch('saveRecordingResponse', scorecardResponse));
      }

      window.Promise.all(promises).then(allDone);
    },
    loadTab(identifier, evt) {
      if (evt && evt.key && evt.key === 'Tab') {
        // Firefox bug fix
        return true;
      }

      const link = this.$refs[identifier.replace(/ /g, '') + 'link'];
      link.blur();
      this.$nextTick(() => {
        const header = document.getElementById(identifier.replace(/ /g, '') + 'header');
        if (header) {
          header.focus();
        }
      });

      this.tab = identifier;
      this.$announcer.set(`The ${identifier} tab has been activated. The associated page content is now available below.`);
    },
    update(feedback) {
      this.saving1 = true;

      const feedbackData = {
        feedbackId: feedback.feedbackId,
        comment: document.getElementById('updated-comment-' + feedback.feedbackId).value,
        groupId: this.recording.groupId,
        recordingId: this.recording.recordingId,
      };
      return this.$store.dispatch('updateFeedback', feedbackData).then(() => {
        this.currentlyEditing = null;
        this.saving1 = false;
        this.$announcer.set('Feedback updated.');
        this.commentRef++;
        this.$forceUpdate();
      });
    }
  }
};
</script>
